/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Client Detail Page - Coach view of client's progress and challenges
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import coachService from '../services/coachService'
import {
  ArrowLeft,
  Target,
  Clock,
  TrendingUp,
  BookOpen,
  CheckCircle,
  PlayCircle,
  FileText,
  Eye,
  Building,
  AlertCircle,
  Bell,
  BarChart3,
  Lock,
  MessageSquare,
  Trophy
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import ClientAnalytics from '../components/ClientAnalytics'
import ClientMessagesTab from '../components/ClientMessagesTab'
import ClientDirectMessagesTab from '../components/ClientDirectMessagesTab'
import toast from 'react-hot-toast'
import { useClientProgress } from '../hooks/useClientProgress'
import {
  PerSubjectScoreDisplay,
  CombinedTrendChart,
  PracticePlanCard,
  PersonalizedRecommendations
} from '../components/scores'
import BadgeDisplay from '../components/badges/BadgeDisplay'
import ActivityHeatmap from '../components/heatmap/ActivityHeatmap'
import StreakTile from '../components/heatmap/StreakTile'
import { createLogger } from '../utils/logger'

const logger = createLogger('ClientDetailPage')

const ClientDetailPage = () => {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [clientData, setClientData] = useState(null)
  const [clientStats, setClientStats] = useState(null)
  const [challenges, setChallenges] = useState([])
  const [pagination, setPagination] = useState({ total: 0, hasMore: false })
  const [loading, setLoading] = useState(true)
  const [challengesLoading, setChallengesLoading] = useState(true)
  // Relationship status from the API: a partially-signed (pending) relationship returns isActive=false
  // plus a readiness breakdown, so we can show a friendly "agreements pending" panel instead of erroring.
  const [relationshipStatus, setRelationshipStatus] = useState(null)
  const [readiness, setReadiness] = useState(null)
  const isPending = relationshipStatus != null && relationshipStatus !== 'active'

  // Get permissions from client data with defaults (used in multiple places)
  const canViewProgress = clientData?.permissions?.canViewProgress !== false

  // Fetch progress data for the client (SAT scores, recommendations, etc.)
  const { data: progressData, loading: progressLoading } = useClientProgress(
    clientId,
    canViewProgress
  )

  // Get permissions from client data with defaults
  const permissions = clientData?.permissions || {
    canViewProgress: true,
    canManageChallenges: true,
    canViewAnalytics: true,
    canSendMessages: true
  }

  // Tab state - will be updated when clientData loads
  const [activeTab, setActiveTab] = useState('progress')

  // Update active tab when client data loads if current tab is not permitted
  useEffect(() => {
    if (clientData) {
      const perms = clientData.permissions || {}
      // If current tab isn't permitted, switch to first available
      const currentTabBlocked =
        (activeTab === 'progress' && perms.canViewProgress === false) ||
        (activeTab === 'challenges' && perms.canManageChallenges === false) ||
        (activeTab === 'analytics' && perms.canViewAnalytics === false) ||
        ((activeTab === 'announcements' || activeTab === 'direct-messages') &&
          perms.canSendMessages === false)

      if (currentTabBlocked) {
        if (perms.canViewProgress !== false) setActiveTab('progress')
        else if (perms.canManageChallenges !== false) setActiveTab('challenges')
        else if (perms.canViewAnalytics !== false) setActiveTab('analytics')
        else if (perms.canSendMessages !== false) setActiveTab('announcements')
      }
    }
  }, [clientData, activeTab])

  // Fetch client details and stats
  useEffect(() => {
    const loadClientData = async () => {
      try {
        setLoading(true)
        const response = await coachService.getClient(clientId)

        if (response.success) {
          setClientData(response.client)
          setClientStats(response.stats)
          // relationshipStatus/isActive are present for both active and pending; default to active for
          // older payloads that omit them.
          setRelationshipStatus(response.relationshipStatus || 'active')
          setReadiness(response.readiness || null)
        }
      } catch (error) {
        logger.error('Error loading client data:', error)
        toast.error('Failed to load client details')
        navigate('/dashboard')
      } finally {
        setLoading(false)
      }
    }

    loadClientData()
  }, [clientId, navigate])

  // Fetch client challenges - only if we have permission
  useEffect(() => {
    const loadChallenges = async () => {
      // Only load if we have canViewProgress permission
      const perms = clientData?.permissions || {}
      if (perms.canViewProgress === false) {
        setChallengesLoading(false)
        return
      }

      try {
        setChallengesLoading(true)
        const response = await coachService.getClientChallenges(clientId, 20, 0)

        if (response.success) {
          setChallenges(response.challenges)
          setPagination(response.pagination)
        }
      } catch (error) {
        logger.error('Error loading challenges:', error)
        // Don't show error toast if it's a permission issue
        if (error.response?.status !== 403) {
          toast.error('Failed to load challenges')
        }
      } finally {
        setChallengesLoading(false)
      }
    }

    // Wait for clientData to be loaded before checking permissions
    if (clientId && clientData) {
      loadChallenges()
    }
  }, [clientId, clientData])

  const loadMoreChallenges = async () => {
    if (!pagination.hasMore) return

    try {
      const response = await coachService.getClientChallenges(clientId, 20, challenges.length)

      if (response.success) {
        setChallenges([...challenges, ...response.challenges])
        setPagination(response.pagination)
      }
    } catch (error) {
      logger.error('Error loading more challenges:', error)
      toast.error('Failed to load more challenges')
    }
  }

  const formatTime = (seconds) => {
    if (!seconds) return '0m'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const getStatusBadge = (status) => {
    const badges = {
      completed: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-300',
        label: 'Completed'
      },
      active: {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-700 dark:text-purple-300',
        label: 'In Progress'
      },
      draft: {
        bg: 'bg-gray-100 dark:bg-gray-800',
        text: 'text-gray-600 dark:text-gray-400',
        label: 'Draft'
      }
    }

    const badge = badges[status] || badges.draft

    return (
      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!clientData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Client not found</p>
      </div>
    )
  }

  // Partially-signed (pending) relationship: tutoring hasn't activated yet, so there's no progress to
  // show. Surface a clear status (who still needs to sign) instead of a privileges error or empty tabs.
  if (isPending) {
    const outstanding = readiness?.missing || []
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {clientData.firstName} {clientData.lastName}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{clientData.email}</p>
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 whitespace-nowrap">
            Agreements Pending
          </span>
        </div>

        <div className="card border border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-50/50 to-indigo-50/50 dark:from-primary-900/10 dark:to-indigo-900/10">
          <div className="card-body">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-primary-100 dark:bg-primary-900/40 rounded-xl">
                <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Tutoring isn&apos;t active yet
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This student accepted, but tutoring can&apos;t begin until the required agreements
                  are signed. Their progress will appear here once everything is signed.
                </p>
              </div>
            </div>

            {outstanding.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  Outstanding
                </p>
                <ul className="space-y-1.5">
                  {outstanding.map((item) => (
                    <li key={item.code} className="flex items-start gap-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.label}
                        {item.party && (
                          <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 capitalize">
                            {item.party}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {clientData.firstName} {clientData.lastName}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{clientData.email}</p>
        </div>
        {clientData.organization && (
          <span className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
            <Building className="w-4 h-4" />
            {clientData.organization.name}
          </span>
        )}
        {/* Limited Access Badge */}
        {(!permissions.canViewProgress ||
          !permissions.canManageChallenges ||
          !permissions.canViewAnalytics ||
          !permissions.canSendMessages) && (
          <span className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
            <Lock className="w-4 h-4" />
            Limited Access
          </span>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6 -mb-px">
          <button
            onClick={() => setActiveTab('progress')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'progress'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Eye className="w-4 h-4" />
            Progress
          </button>
          <button
            onClick={() => setActiveTab('challenges')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'challenges'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Challenges
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'analytics'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('announcements')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'announcements'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Bell className="w-4 h-4" />
            Announcements
          </button>
          <button
            onClick={() => setActiveTab('direct-messages')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'direct-messages'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Messages
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'progress' && (
        <>
          {!permissions.canViewProgress ? (
            <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Lock className="w-5 h-5" />
                <p className="font-medium">Progress viewing not permitted</p>
              </div>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                The student has limited your access. Contact them to request additional permissions.
              </p>
            </div>
          ) : !clientStats ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading statistics...</span>
            </div>
          ) : (
            <>
              {/* Statistics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Challenges */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Challenges</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {clientStats.totalChallenges}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Completed */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {clientStats.completedChallenges}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* In Progress */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <PlayCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">In Progress</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {clientStats.inProgressChallenges}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Average Score */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Target className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Average Score</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {clientStats.averageScore !== null
                            ? `${Math.round(clientStats.averageScore * 10) / 10}%`
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Accuracy Rate
                      </p>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {clientStats.accuracyRate !== null
                        ? `${Math.round(clientStats.accuracyRate * 10) / 10}%`
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Time Spent
                      </p>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {formatTime(clientStats.totalTimeSpent)}
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Questions Answered
                      </p>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {clientStats.totalQuestionsAnswered}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {clientStats.totalCorrectAnswers} correct
                    </p>
                  </div>
                </div>
              </div>

              {/* Activity Heatmap & Streak Section - Right after stats */}
              {!progressLoading && progressData.activityHeatmap && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ActivityHeatmap
                    activities={progressData.activityHeatmap.activities}
                    stats={progressData.activityHeatmap.stats}
                    className="w-full"
                  />
                  <StreakTile stats={progressData.activityHeatmap.stats} />
                </div>
              )}

              {/* Enhanced Progress Sections */}
              {progressLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    Loading detailed progress...
                  </span>
                </div>
              ) : (
                <>
                  {/* 1. SAT Score Section */}
                  {progressData.perSubjectScores && (
                    <div className="card">
                      <div className="card-header">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-primary-600" />
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            SAT Scores
                          </h2>
                        </div>
                      </div>
                      <div className="card-body">
                        <PerSubjectScoreDisplay
                          mathData={progressData.perSubjectScores.math}
                          readingWritingData={progressData.perSubjectScores.readingWriting}
                          combinedData={progressData.perSubjectScores.combined}
                          showCurrent={true}
                          showPredicted={true}
                          showTrend={true}
                        />
                      </div>
                    </div>
                  )}

                  {/* 2. Score Trend Section */}
                  {(progressData.mathActualHistory || progressData.rwActualHistory) && (
                    <div className="card">
                      <div className="card-header">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-primary-600" />
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Score Trend
                          </h2>
                        </div>
                      </div>
                      <div className="card-body">
                        <CombinedTrendChart
                          mathActualHistory={progressData.mathActualHistory?.history || []}
                          rwActualHistory={progressData.rwActualHistory?.history || []}
                          mathPredictedHistory={
                            progressData.perSubjectScores?.math?.trend?.history || []
                          }
                          rwPredictedHistory={
                            progressData.perSubjectScores?.readingWriting?.trend?.history || []
                          }
                          mathUncertainty={progressData.perSubjectScores?.math?.uncertaintyBand}
                          rwUncertainty={
                            progressData.perSubjectScores?.readingWriting?.uncertaintyBand
                          }
                          goalScore={progressData.goal?.goalSatScore}
                          goalDate={progressData.practiceRecommendation?.testDate}
                          height={280}
                          showConfidenceBands={true}
                          showProjections={true}
                        />
                      </div>
                    </div>
                  )}

                  {/* 3. Practice Plan Section (read-only) */}
                  {progressData.goal?.goalSatScore && progressData.practiceRecommendation && (
                    <PracticePlanCard data={progressData.practiceRecommendation} readOnly={true} />
                  )}

                  {/* 4. Personalized Recommendations (read-only) */}
                  {progressData.goal?.goalSatScore && (
                    <PersonalizedRecommendations
                      recommendations={progressData.recommendations}
                      loading={false}
                      readOnly={true}
                    />
                  )}

                  {/* 5. Recent Achievements Section */}
                  <div className="card">
                    <div className="card-header">
                      <div className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Recent Achievements
                        </h2>
                      </div>
                    </div>
                    <div className="card-body">
                      <BadgeDisplay userId={clientId} />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <>
          {!permissions.canViewAnalytics ? (
            <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Lock className="w-5 h-5" />
                <p className="font-medium">Analytics viewing not permitted</p>
              </div>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                The student has limited your access. Contact them to request additional permissions.
              </p>
            </div>
          ) : (
            <>
              {/* Performance Analytics */}
              {!challengesLoading && challenges.length > 0 ? (
                <ClientAnalytics challenges={challenges} stats={clientStats} />
              ) : challengesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">No analytics data yet</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    Analytics will appear once {clientData.firstName} completes some challenges
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Challenges Tab */}
      {activeTab === 'challenges' && (
        <>
          {!permissions.canManageChallenges ? (
            <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Lock className="w-5 h-5" />
                <p className="font-medium">Challenge management not permitted</p>
              </div>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                The student has limited your access. Contact them to request additional permissions.
              </p>
            </div>
          ) : (
            <>
              {/* Challenges List */}
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Challenge History
                    </h2>
                    {pagination.total > 0 && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {challenges.length} of {pagination.total}
                      </span>
                    )}
                  </div>

                  {challengesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        Loading challenges...
                      </span>
                    </div>
                  ) : challenges.length === 0 ? (
                    <div className="text-center py-8">
                      <BookOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">No challenges yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {challenges.map((challenge) => (
                        <div
                          key={challenge.id}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                                  {challenge.name || 'SAT Practice Challenge'}
                                </h3>
                                {getStatusBadge(challenge.status)}
                              </div>

                              {challenge.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                  {challenge.description}
                                </p>
                              )}

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                    Score
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {challenge.totalScore !== null
                                      ? `${Math.round(challenge.totalScore * 10) / 10}%`
                                      : 'N/A'}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                    Questions
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {challenge.correctAnswers || 0}/{challenge.totalQuestions || 0}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                    Time
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {formatTime(challenge.totalTime)}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                    Date
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {new Date(challenge.createdAt).toLocaleDateString(undefined, {
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* View Details Button */}
                            {challenge.status === 'completed' && (
                              <button
                                onClick={() => navigate(`/challenge/${challenge.id}/report`)}
                                className="btn btn-sm btn-secondary inline-flex items-center gap-1 whitespace-nowrap"
                              >
                                <Eye className="w-4 h-4" />
                                <span className="hidden sm:inline">View</span>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Load More Button */}
                      {pagination.hasMore && (
                        <div className="text-center pt-4">
                          <button onClick={loadMoreChallenges} className="btn btn-secondary">
                            Load More
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <ClientMessagesTab
          clientId={clientId}
          clientName={`${clientData.firstName} ${clientData.lastName}`}
          permissions={permissions}
        />
      )}

      {/* Direct Messages Tab */}
      {activeTab === 'direct-messages' && (
        <ClientDirectMessagesTab
          clientId={clientId}
          clientName={`${clientData.firstName} ${clientData.lastName}`}
          currentUserId={user?.id}
          permissions={permissions}
        />
      )}
    </div>
  )
}

export default ClientDetailPage
